import _ from 'lodash';
import React from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { scroller } from 'react-scroll';
import { useChangeAwareEffect } from 'use-change-aware-effect';
import { recordStatisticsEvent } from '../../services/statistics-service';

const slowScrollOpts = {
  offset: 100,
  duration: 1500,
  smooth: 'easeInOutQuint',
};

const gwl = (d, name) => _.get(d, `${name}.length`);

export default function useScrollableWires({
  type, isScrollable, useGetWires, initNumber, entityName, wireName,
}) {
  const [initial, setInitial] = React.useState(true);
  const [showExpand, setShowExpand] = React.useState(false);
  const [total, setTotal] = React.useState(0);
  const [mentionsTotal, setMentionsTotal] = React.useState(0);
  const [wires, setWires] = React.useState([]);
  const [page, setPage] = React.useState(1);
  const [hasNextPage, setHasNextPage] = React.useState(false);
  const [rawData, setRawData] = React.useState({});
  const [{ loading }, getWires] = useGetWires();

  const scrollToNextPage = React.useCallback(() => scroller.scrollTo(
    wireName(type, wires.length - (type === 'mentions' ? 2 : 1)),
    slowScrollOpts,
  ), [type, wires, wireName]);

  const expandList = React.useCallback(e => {
    e && e.preventDefault();
    if (gwl(rawData, entityName) > initNumber) {
      setWires(rawData[entityName]);
      setHasNextPage(rawData.total > wires.length);
    }
    recordStatisticsEvent({
      event: 'see_all_results_click',
      statisticable: 'navigation',
      id: 0,
      custom_data: { page: window.location.href },
    });
  }, [rawData, wires, initNumber, entityName]);

  const onData = React.useCallback(({ data }) => {
    if (!gwl(data, entityName)) {
      return;
    }

    setInitial(false);
    setRawData(data);
    if (wires.length === 0) {
      const newWires = _.slice(data[entityName], 0, initNumber);
      setWires(newWires);
      setTotal(data.total);
      setMentionsTotal(data.mentionsTotal || 0);
    } else {
      const newWires = [...wires, ...data[entityName]];
      setHasNextPage(total > newWires.length);
      setWires(newWires);
    }
  }, [wires, total, initNumber, entityName]);

  const onLoadMore = React.useCallback(() => {
    setHasNextPage(initial || (total > wires.length));
    const newPage = page + 1;
    getWires(newPage).then(onData);
    setPage(newPage);
  }, [initial, wires, total, page, getWires, onData]);

  const [infiniteRef] = useInfiniteScroll({ loading, hasNextPage, onLoadMore });

  useChangeAwareEffect(({ isMount }) => {
    isMount && getWires(page).then(onData);
  }, {});

  React.useEffect(() => {
    const fullBucket = wires.length === initNumber;
    const moreFromRawData = gwl(rawData, entityName) > initNumber;
    setShowExpand(fullBucket && moreFromRawData && !isScrollable);

    if (isScrollable && page === 1) {
      expandList();
      scrollToNextPage();
    }
  }, [wires, rawData, page, expandList, scrollToNextPage, isScrollable, initNumber, entityName]);

  return {
    expandList,
    infiniteRef,
    loading,
    mentionsTotal,
    rawData,
    showExpand,
    total,
    wires,
    hasMore: showExpand || initial || hasNextPage,
  };
}
